/**
 * Guard against unnecessary morphs of turbo-frames in the dashboard.
 * Each frame has a data-cache-token attribute computed from the item's
 * content fingerprint (query + item attributes + cascaded themes). If the
 * token is unchanged, we can skip the morph and keep the existing frame.
 *
 * This is a performance optimization to avoid unnecessary re-renders of charts and tables.
 * It also preserves the state of any interactive elements within the frame.
 */
document.addEventListener('turbo:before-morph-element', (event) => {
  const {
    target: existingElement,
    detail: { newElement }
  } = event;

  if (existingElement.tagName !== 'TURBO-FRAME') return;

  const oldToken = existingElement?.dataset?.cacheToken;
  const newToken = newElement?.dataset?.cacheToken;

  if (!oldToken || !newToken) return;
  if (oldToken === newToken) event.preventDefault();
});

/**
 * Protect attributes that hold pure client-side state from being touched
 * by morph. The server never renders these values, so without this guard
 * Idiomorph would reset them on every morph — closing modals, snapping
 * the chat panel shut, undoing drag-positioned chrome, etc.
 *
 *   - <dialog open>             — set by showModal(); morph would close it
 *   - #charting-chat data-open  — chat FAB/panel open/closed state
 *   - chat panel data-open      — same state, on the inner panel target
 *   - chat panel style          — drag position written by the controller
 */
document.addEventListener('turbo:before-morph-attribute', (event) => {
  const { target, detail } = event;
  const attr = detail.attributeName;

  if (target instanceof HTMLDialogElement && attr === 'open') {
    event.preventDefault();
    return;
  }

  if (target.classList?.contains('charting-chat') && attr === 'data-open') {
    event.preventDefault();
    return;
  }

  if (target.matches?.('[data-charting-chat-target="panel"]') && (attr === 'data-open' || attr === 'style')) {
    event.preventDefault();
  }
});
